import { ArrowRight, Wallet } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/primitives/button";

interface Props {
  /** Add-on credits the user holds but cannot currently spend. */
  balance: number;
  /** Opens the plans / checkout so they can resubscribe and unlock the balance. */
  onResubscribe: () => void;
}

/**
 * Read-only panel for a free / churned user who holds add-on credits that are
 * frozen (no active subscription). It surfaces the waiting balance and a
 * resubscribe nudge — but NO buy/purchase action, since top-ups are only
 * available to active subscribers (free-trial-contract@2 §4 addOns.spendable).
 */
export default function StrandedCreditsPanel({
  balance,
  onResubscribe,
}: Props) {
  const { t } = useTranslation();
  // Floor for display, guarding non-finite / negative values from a malformed
  // DTO so we never render "NaN credits waiting" (mirrors TrialStrip's guard).
  const credits = Number.isFinite(balance)
    ? Math.max(0, Math.floor(balance))
    : 0;

  return (
    <div className="relative flex min-h-full items-center justify-center px-5 py-12">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 30%, hsl(var(--secondary) / 0.10), transparent 60%)",
        }}
      />
      <div className="relative w-full max-w-[460px] rounded-[14px] border border-border bg-card px-6 py-8 text-center">
        <div className="mx-auto mb-3.5 flex size-12 items-center justify-center rounded-xl bg-secondary/10 text-secondary">
          <Wallet size={22} />
        </div>
        <p className="mb-2 text-[10.5px] font-bold uppercase tracking-[0.16em] text-secondary">
          {t("payments.stranded.eyebrow")}
        </p>
        <h2 className="mb-2.5 text-[18px] font-semibold leading-snug tracking-tight">
          {t("payments.stranded.title", {
            count: credits,
            credits: credits.toLocaleString(),
          })}
        </h2>
        <p className="mx-auto mb-6 max-w-sm text-[13px] leading-relaxed text-muted-foreground">
          {t("payments.stranded.body")}
        </p>
        <Button
          onClick={onResubscribe}
          className="h-11 w-full gap-2 text-[15px] font-semibold"
        >
          {t("payments.stranded.cta")}
          <ArrowRight size={16} />
        </Button>
      </div>
    </div>
  );
}
